import {
  ViDamageType,
  ViGenericPartName,
  ViSide,
  ViTotalLossLabel,
  ViInferenceClaimDamagedPart,
  ViImageCategory,
} from "./";

export interface ViCoreOrigin {
  system: string;
  caseId: string;
  customerId: string;
  country: string;
}

export interface ViCoreCarModel {
  model: {
    axModelCodeInt: string;
    axManufacturerCodeInt: string;
  };
  p: number;
  consideredImages?: ViCoreConsideredImages;
}

export interface ViCoreConsideredImages {
  id: string;
  importance: number;
}

export interface ViCorePostCaseResponse {
  id: string;
  origin: ViCoreOrigin;
  [key: string]: any;
}

export class ViCorePostCaseRequest {
  origin: ViCoreOrigin;
  carModel: { manufacturerName: string; modelName: string };
  constructor(
    origin: ViCoreOrigin,
    carModel: { manufacturerName: string; modelName: string }
  ) {
    this.origin = origin;
    this.carModel = carModel;
  }
}

export interface ViCorePostImageResponse {
  images: ViCoreImage[];
  warnings: any[];
}

export interface ViCoreImage {
  id: string; //image hash
  status: ViCoreImageState;
  metaData: {
    filename?: string;
    dimensions?: { width: number; height: number };
  };
  clientInput?: { category: string; side: string };
  warnings?: ViCoreWarning[];
  error?: ViCoreError;
  results?: { category?: { label: ViImageCategory; p: number } };
}

export interface ViCoreGetProcessingResponse {
  id: string;
  caseId: string;
  status: ViCoreCaseStatus;
  images: ViCoreImage[];
  inferenceFlow: ViCoreProcessingResponseInferenceFlow;
  requestInfo: any;
}

export interface ViCoreProcessing {
  id?: string;
  aggregatedPartDamages: ViCoreAggregatedPartDamage[];
  claimDamagedParts: ViInferenceClaimDamagedPart[];
  claimTotalLosses?: ViCoreClaimTotalLoss[];
  claimCarModels?: ViCoreCarModel[];
  images: ViCoreProcessingImage[];
  meanConfidence?: number;
  numParts?: number;
  timing?: number;
}

interface ViCoreProcessingResponseInferenceFlow {
  status: "COMPLETED";
  timestamps: any;
  images: ViCoreProcessingImage[];
  results: {
    aggregatedObjectDetectionPartDamages: any;
    aggregatedParts: any;
    aggregatedPartDamages: ViCoreAggregatedPartDamage[];
    claimDamagedParts: ViInferenceClaimDamagedPart[];
    claimTotalLosses?: ViCoreClaimTotalLoss[];
    claimCarModels: ViCoreCarModel[];
    experimental?: any;
  };
}

export interface ViCoreProcessingImage {
  id: string;
  results: {
    imageCategories: ViCoreImageCategory[];
    imageSides: VICoreImageSide[];
    imageParts: ViCoreImagePart[];
    imageDamages: ViCoreImageDamage[];
    imagePartDamages: ViCoreImagePartDamage[];
    experimental: any;
  };
}

export interface ViCoreImageCategory {
  label: ViImageCategory;
  p: number;
  predetermined: false;
}

export interface VICoreImageSide {
  side: VICoreImageSide;
  p: number;
  predetermined: false;
}

export interface ViCoreAggregatedPartDamage {
  id: string;
  type: ViDamageType;
  identifiers: ViCorePartIdentifiers;
  relativeSize: number;
  p: number;
  imagePartDamageIds: string[];
  claimDamagedPartId: string;
}

export interface ViCorePartIdentifiers {
  genericPartName: ViGenericPartName;
  side: ViSide;
}

export interface ViCoreImagePartDamage {
  id: string;
  imageDamageId: string;
  imagePartId: string;
  overlap: number;
  imageId: string;
  p: number;
  relativeSize: number;
}

export interface ViCoreImageDamage {
  id: string;
  bbox: [number, number][];
  contour: [number, number][];
  p: number;
  type: ViDamageType;
}

export interface ViCoreImagePart {
  id: string;
  bbox: [number, number][];
  contour: [number, number][];
  identifiers: ViCorePartIdentifiers;
  p: number;
}

export interface ViCoreClaimTotalLoss {
  totalLoss: ViTotalLossLabel;
  p: number;
  consideredImages?: ViCoreConsideredImages;
}

export interface ViCoreError {
  code: string;
  description: string;
  payload?: { [key: string]: string };
}

export interface ViCoreWarning {
  code: string;
  description: string;
  payload?: { [key: string]: string };
}

export enum ViCoreImageState {
  "ACTIVE" = "ACTIVE",
  "INACTIVE" = "INACTIVE",
  "REJECTED" = "REJECTED",
}

export enum ViCoreCaseStatus {
  "IN_PROGRESS" = "IN_PROGRESS",
  "COMPLETED" = "COMPLETED",
  "FAILED" = "FAILED",
}

export enum ViCoreIssueCodes {
  "ImageAlreadyExists" = "I409.1",
  "TooManyActiveImages" = "I413.2",
  "TooManyImagesInRequest" = "I413.3",
}
